import React, { useState, useCallback, useRef } from "react";
import { getPrecision } from "../../../../utils/tillHelpers";
import { Modal, Card, Row, Col, Input, Button } from "antd";
import { TagsOutlined } from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import sortBy from "lodash/sortBy";
import AspectRatio from "react-aspect-ratio";
import CashLogo from "../../../../assets/images/cash.png";
import CashLogoWhite from "../../../../assets/images/cashWhite.svg";
import Delete from "../../../../assets/images/delete.png";
import Backspace from "../../../../assets/images/backspac.png";
import closeIcon from "../../../../assets/images/closeicon.png";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { KEYPAD_NO_FOCUS } from "../../../../lib/keypadNoFocus";

/**
 * Payment Method selection modal with amount keypad.
 *
 * Displays available payment methods on the left and an amount input
 * with a numeric keypad on the right.
 *
 * @param {Object} props
 * @param {boolean} props.paymentModal - Whether the modal is visible
 * @param {Object} props.tillData - Till configuration data
 * @param {Array} props.tillDataPaymentMethods - Available payment methods
 * @param {Object} props.posConfig - POS configuration
 * @param {Object} props.cart - Current cart state
 * @param {Object} props.selectedPaymentMethod - Currently selected payment method
 * @param {Function} props.setSelectedPaymentMethod - Setter for selected payment method
 * @param {Function} props.openPaymentModalByCustomer - Opens payment modal by customer context
 * @param {Object} props.paymentCardRef - Ref for the first payment card
 * @param {Object} props.paymentModalInputRef - Ref for the payment amount input
 * @param {string|number} props.amount - Current amount value
 * @param {Function} props.onChangeAmount - Handler for amount input change
 * @param {Function} props.handleAmount - Handler for keypad button presses
 * @param {Function} props.requestPayment - Processes a payment request
 * @param {Object} props.paymentModalLoyalityMessages - Loyalty-related messages
 * @param {Function} props.processOrder - Processes the order
 * @param {Function} props.setCouponModalVisible - Setter for coupon modal visibility
 * @param {Function} props.closePaymentModal - Handler to close the payment modal
 * @param {Array} props.currenciesList - Available currencies
 * @param {number} props.width - Current window width for responsive sizing
 * @param {Object} props.data - Style configuration (retailPos.json)
 */
const PaymentMethodModal = ({
  paymentModal,
  tillData,
  tillDataPaymentMethods,
  posConfig,
  cart,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  openPaymentModalByCustomer,
  paymentCardRef,
  paymentModalInputRef,
  amount,
  onChangeAmount,
  handleAmount,
  requestPayment,
  paymentModalLoyalityMessages,
  processOrder,
  setCouponModalVisible,
  closePaymentModal,
  currenciesList,
  width,
  data,
  setShowPaymentDataPopup,
  setPaymentDataPopupMethod,
  setPaymentDataPopupAmount,
}) => {
  const isExchange = useTransactionStore((s) => s.selectedReturnType) === "EXCHANGE";
  const exchangeCart = useCartStore((s) => s.exchangeCart);
  // BUG-1563 diagnostic — log the routing decision on every render so we can
  // confirm in the browser console whether needsPayment is true/false and
  // whether Enter would call requestPayment or processOrder.
  const needsPayment = isExchange
    ? parseFloat((exchangeCart?.total || 0) + (cart.total || 0)) > parseFloat(exchangeCart?.paid || 0)
    : parseFloat(cart.total) > parseFloat(cart.paid); // was >=: caused paid===total to still route to requestPayment

  // Double-click/rapid-Enter protection: useRef guard is synchronous so it
  // blocks every call after the first within the same JS tick, unlike useState
  // which batches updates and leaves stale closures open to concurrent calls.
  const [processingOrder, setProcessingOrder] = useState(false);
  const processingOrderRef = useRef(false);
  const guardedProcessOrder = useCallback(async (...args) => {
    if (processingOrderRef.current) return;
    processingOrderRef.current = true;
    setProcessingOrder(true);
    try {
      await processOrder(...args);
    } finally {
      processingOrderRef.current = false;
      setProcessingOrder(false);
    }
  }, [processOrder]);
  const guardedRequestPayment = useCallback(async (...args) => {
    if (processingOrderRef.current) return;
    processingOrderRef.current = true;
    setProcessingOrder(true);
    try {
      await requestPayment(...args);
    } finally {
      processingOrderRef.current = false;
      setProcessingOrder(false);
    }
  }, [requestPayment]);

  return (
    <Modal open={paymentModal} keyboard={false} closable={false} footer={null} width="60%" centered styles={{ body: { padding: 0 } }}>
      <Card styles={{ body: { padding: 15 } }}>
        <Row gutter={[10, 0]}>
          <Col span={12}>
            <Card style={data.posScreenStyles.restaurantPaymentModal.noBorderCard} styles={{ body: { padding: "10px" } }}>
              <div style={tillData.tillAccess.cwrTill.layAway === "Y" && tillData.tillAccess.cwrTill.payNow === "N" ? { pointerEvents: "none", opacity: "0.4" } : {}}>
                {/*  <p className="para-margin" /> */}
                <Row>
                  <Col span={24}>
                    <Scrollbars style={data.posScreenStyles.restaurantPaymentModal.scrollValue}>
                      {sortBy(tillDataPaymentMethods, "sequenceNo").map((pm, ind) => {
                        return posConfig?.loyaltyApplicable === "N" && pm.isloyalty ? null : (
                          <Card
                            key={pm?.finPaymentmethodId}
                            onClick={() => {
                              if (cart.items.length > 0) {
                                setSelectedPaymentMethod(pm);
                                openPaymentModalByCustomer();
                              }
                            }}
                            ref={ind === 0 ? paymentCardRef : null}
                            style={data.posScreenStyles.restaurantPaymentModal.noBorderCard}
                            tabIndex={0}
                            onKeyPress={(e) => (e.charCode === 13 ? setSelectedPaymentMethod(pm) : null)}
                            styles={{ body: { padding: "0px", marginTop: "15px" } }}
                          >
                            <Row>
                              <Col span={24}>
                                <Card
                                  style={
                                    pm?.finPaymentmethodId === selectedPaymentMethod?.finPaymentmethodId
                                      ? data.posScreenStyles.restaurantPaymentModal.selectedPaymentType
                                      : data.posScreenStyles.restaurantPaymentModal.paymtCard
                                  }
                                  // className={
                                  //   pm.finPaymentmethodId ===
                                  //   selectedPaymentMethod.finPaymentmethodId
                                  //     ? "selectedPaymentType"
                                  //     : "paymt-card"
                                  // }
                                  styles={{ body: { padding: "8px 0px" } }}
                                >
                                  <Row>
                                    <Col span={14}>
                                      <div style={{ display: "flex" }}>
                                        <img
                                          src={pm?.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId ? CashLogoWhite : CashLogo}
                                          style={data.posScreenStyles.restaurantPaymentModal.wfifty}
                                          alt=""
                                        />{" "}
                                        <p
                                          style={
                                            pm?.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                              ? data.posScreenStyles.restaurantPaymentModal.selectedPaymentTypeName
                                              : data.posScreenStyles.restaurantPaymentModal.paymtName
                                          }
                                        >
                                          {pm.name}
                                        </p>
                                      </div>
                                      {cart.printedName && pm?.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId ? (
                                        <p style={{ color: "white", margin: 0 }}>
                                          {cart.printedName} -{" "}
                                          {currenciesList[0].symbolRightSide === "N" && pm?.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                            ? currenciesList[0].currSymbol
                                            : ""}{" "}
                                          {cart.fee}
                                        </p>
                                      ) : null}
                                    </Col>

                                    <Col span={10} style={{ alignSelf: "center" }}>
                                      {(() => {
                                        const iN = cart?.payments?.findIndex((paymentMade) => paymentMade?.name === pm.name);
                                        if (iN >= 0) {
                                          return (
                                            <Row>
                                              <Col span={20}>
                                                <span
                                                  style={
                                                    pm?.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                                      ? data.posScreenStyles.restaurantPaymentModal.selectedPaymentTypeName
                                                      : data.posScreenStyles.restaurantPaymentModal.paymtName
                                                  }
                                                >
                                                  {currenciesList[0]?.symbolRightSide === "N" ? pm.currSymbol : ""} {parseFloat(cart.payments[iN].amount).toFixed(2)}{" "}
                                                  {currenciesList[0].symbolRightSide === "Y" ? pm.currSymbol : ""}
                                                </span>
                                              </Col>
                                              <Col span={4}>
                                                <span style={data.posScreenStyles.restaurantPaymentModal.deleteIconSpan}>
                                                  <img
                                                    src={Delete}
                                                    onClick={() => requestPayment(cart.payments[iN], -cart.payments[iN].amount, "allow")}
                                                    style={data.posScreenStyles.restaurantPaymentModal.deleteIcon}
                                                    alt=""
                                                  />
                                                </span>
                                              </Col>
                                            </Row>
                                          );
                                        }
                                      })()}
                                    </Col>
                                  </Row>
                                </Card>
                              </Col>
                            </Row>
                          </Card>
                        );
                      })}
                    </Scrollbars>
                  </Col>
                </Row>
                <Row style={{ paddingTop: 10 }}>
                  <Button
                    type="dashed"
                    size="large"
                    disabled={cart.mPricingCouponId ? true : false}
                    onClick={() => setCouponModalVisible(true)}
                    className="add-payment-btn-white"
                    style={data.posScreenStyles.restaurantPaymentModal.addpaymentbtnWhite}
                  >
                    <TagsOutlined /> Apply Coupon
                  </Button>
                </Row>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              style={data.posScreenStyles.restaurantPaymentModal.noBorderCard}
              styles={{ body: {
                padding: "10px",
                /*  backgroundColor: "#F5F5F5",
                  borderRadius: "0px 0px 6px 6px", */
              } }}
            >
              <Row style={{ marginBottom: 10 }}>
                <Col span={12}>
                  {posConfig?.loyaltyApplicable === "Y" && (
                    <span style={data.posScreenStyles.restaurantPaymentModal.topayEntrAmt}>
                      Loyalty Bal:&nbsp;
                      <span
                        style={{
                          marginBottom: "0px",
                          color: "#030d1c",
                          fontSize: "1.5vw",
                        }}
                      >
                        {cart?.customer?.retlLoyaltyBalance || 0}
                      </span>
                    </span>
                  )}
                </Col>
                <Col span={12}>
                  <span style={data.posScreenStyles.restaurantPaymentModal.deleteIconSpan}>
                    <img src={closeIcon} alt="close" onClick={closePaymentModal} />
                  </span>
                </Col>
              </Row>
              {/* <Row>
                <Col offset={12} span={12}>
                  <br />
                </Col>
              </Row> */}
              <Row>
                <Col span={12}>
                  <span style={data.posScreenStyles.restaurantPaymentModal.cartTotalMethod}>{needsPayment ? "To Pay" : "Change"}</span>
                </Col>
                <Col span={12}>
                  <span style={data.posScreenStyles.restaurantPaymentModal.cartTotalValue}>
                    {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                    &nbsp;
                    {needsPayment
                      ? isExchange
                        ? parseFloat((exchangeCart?.total || 0) + (cart.total || 0) - (exchangeCart?.paid || 0)).toFixed(getPrecision(tillData))
                        : cart.convertionTotal !== undefined
                          ? cart.convertionTotal
                          : cart.total
                      : parseFloat(isExchange ? (exchangeCart?.change || 0) : cart.changedValue).toFixed(getPrecision(tillData))}{" "}
                    {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                  </span>
                </Col>
              </Row>
            </Card>
            <Card style={data.posScreenStyles.restaurantPaymentModal.noBorderCard} styles={{ body: { padding: "10px" } }}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <h3 style={data.posScreenStyles.restaurantPaymentModal.topayEntrAmt}>{paymentModalLoyalityMessages.inputMessage}</h3>
                </Col>
                {/* <p className="para-margin" /> */}
                {/* <Row> */}
                <Col span={24}>
                  <Input
                    ref={paymentModalInputRef}
                    value={amount}
                    onChange={onChangeAmount}
                    disabled={tillData.tillAccess.cwrTill.layAway === "Y" && tillData.tillAccess.cwrTill.payNow === "N"}
                    onPressEnter={() => {
                      if (needsPayment) {
                        guardedRequestPayment(selectedPaymentMethod, amount);
                      } else {
                        guardedProcessOrder();
                      }
                    }}
                    style={data.posScreenStyles.restaurantPaymentModal.topayInput}
                    size="large"
                  />
                </Col>
                {/* </Row> */}
                {/* <p className="para-margin" /> */}
                {/* <Row style={{ paddingTop: 10 }}> */}
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("1")}>
                      1
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("2")}>
                      2
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("3")}>
                      3
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("x")}>
                      <img src={Backspace} alt="back space" width={width <= 1024 ? 20 : 30} height={width <= 1024 ? 15 : 20} />
                    </button>
                  </AspectRatio>
                </Col>
                {/* </Row> */}
                {/* <Row style={{ paddingTop: 15 }}> */}
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("4")}>
                      4
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("5")}>
                      5
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("6")}>
                      6
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount(".")}>
                      .
                    </button>
                  </AspectRatio>
                </Col>
                {/* </Row> */}
                {/* <Row style={{ paddingTop: 15 }}> */}
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("7")}>
                      7
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("8")}>
                      8
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("9")}>
                      9
                    </button>
                  </AspectRatio>
                </Col>
                <Col span={6}>
                  <AspectRatio ratio="1">
                    <button {...KEYPAD_NO_FOCUS} className="amt-dial-btn" onClick={() => handleAmount("0")}>
                      0
                    </button>
                  </AspectRatio>
                </Col>
                {/* </Row> */}
                {/* <p className="para-margin" /> */}
                {/* <Row style={{ paddingTop: 18 }}> */}
                {tillData.tillAccess.cwrTill.payNow === "Y" ? (
                  <Button
                    size="large"
                    onClick={
                      needsPayment
                        ? () => {
                            // TSK-2934: selectedPaymentMethod.enablePaymentData (per-payment-method,
                            // from getTillData) is unrelated to posConfig.enablePaymentData used elsewhere.
                            if (selectedPaymentMethod?.enablePaymentData && selectedPaymentMethod?.paymentDataFields?.length > 0) {
                              setPaymentDataPopupMethod(selectedPaymentMethod);
                              setPaymentDataPopupAmount(amount);
                              setShowPaymentDataPopup(true);
                              return;
                            }
                            guardedRequestPayment(selectedPaymentMethod, amount);
                          }
                        : () => guardedProcessOrder()
                    }
                    disabled={processingOrder}
                    loading={processingOrder}
                    style={data.posScreenStyles.restaurantPaymentModal.addPaymentBtn}
                  >
                    {cart.items.length !== 0
                      ? needsPayment
                        ? paymentModalLoyalityMessages.buttonText
                        : cart.payments?.length > 0 || (isExchange && exchangeCart?.payments?.length > 0)
                          ? "Order"
                          : paymentModalLoyalityMessages.buttonText
                      : "Payment Not Allowed !"}
                  </Button>
                ) : null}
                {tillData.tillAccess.cwrTill.layAway === "Y" ? (
                  <Button
                    size="large"
                    onClick={() => guardedProcessOrder("layaway")}
                    disabled={processingOrder}
                    loading={processingOrder}
                    style={{ ...data.posScreenStyles.restaurantPaymentModal.addPaymentBtn, backgroundColor: "#708090", border: "1px solid #708090" }}
                  >
                    {cart.items.length !== 0 ? "Layaway" : "Operation Not Allowed !"}
                  </Button>
                ) : null}
                {cart?.creditSale === "Y" ? (
                  <Button
                    size="large"
                    onClick={() => guardedProcessOrder("layaway")}
                    disabled={processingOrder}
                    loading={processingOrder}
                    style={{ ...data.posScreenStyles.restaurantPaymentModal.addPaymentBtn, backgroundColor: "#2F3856", border: "1px solid #2F3856" }}
                  >
                    {cart.items.length !== 0 ? "Credit Sale" : "Operation Not Allowed !"}
                  </Button>
                ) : null}
              </Row>
            </Card>
          </Col>
        </Row>
      </Card>
    </Modal>
  );
};

export default PaymentMethodModal;
